import React from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import { AssignmentAssociate } from '../../assignments/models';
import { getAssociateDisplayLabel } from '../../utils';
import AssociateAvatar from '../../components/AssociateAvatar';
import { AssociateAssignment, UNASSIGNED_LABEL } from '../utils';

export interface AssociateRowProps {
  associate: AssignmentAssociate;
  /**
   * The associate's derived assignment, split into process-path name and an
   * optional station suffix so the card can render them as two styled lines.
   */
  assignment: AssociateAssignment;
}

/**
 * A single associate card on the Staffing board (Associate View Page).
 *
 * Pure presentational. Matches the staffing-board mock: a compact horizontal
 * card with three regions —
 *   - Left:   circular avatar showing the associate's badge photo (from
 *             `associate.associateAlias`), falling back to 1–2 character
 *             initials derived from `associate.associateName`.
 *   - Middle: three stacked lines:
 *       • login (the associate's alias, or full name when no alias) in bold;
 *       • process-path name in the board's accent color;
 *       • station (workstation name) in muted gray — omitted when empty
 *         (department placements and unassigned associates have no station).
 *
 * The component is strictly read-only: no click handlers, no buttons, no
 * inputs, no mutation controls of any kind. Layout uses Meridian `Box` and
 * text is rendered via Meridian `Text`; the fine-grained card / avatar
 * styling (sizes, colors, borders) lives in the page-level SCSS
 * (`index.scss`), which keys off the `staffing-card` class names below.
 *
 * The root element carries `data-testid="associate-view-row"` plus
 * `data-associate-id={associate.associateId}` so tests can target a specific
 * card without relying on DOM order.
 */
const AssociateRow: React.FC<AssociateRowProps> = ({ associate, assignment }) => {
  const login = getAssociateDisplayLabel(
    associate.associateAlias,
    associate.associateName
  );
  const { processPathName, station } = assignment;
  const isUnassigned = processPathName === UNASSIGNED_LABEL;

  return (
    <Box
      className="staffing-card"
      data-testid="associate-view-row"
      data-associate-id={associate.associateId}
    >
      <AssociateAvatar
        associateName={associate.associateName}
        associateAlias={associate.associateAlias}
        className="staffing-card__avatar"
      />
      <Box className="staffing-card__info">
        <Text
          type="b200"
          truncate
          className="staffing-card__login"
          data-testid="staffing-card-login"
        >
          {login}
        </Text>
        <Text
          type="b100"
          truncate
          // An "Unassigned" path is muted via a dedicated modifier class rather
          // than Meridian's `color="secondary"` prop: the page SCSS applies
          // `color: <accent> !important` to `.staffing-card__path`, which would
          // override Meridian's inline secondary color. The modifier class wins
          // by carrying its own `!important` in the same stylesheet.
          className={
            isUnassigned
              ? 'staffing-card__path staffing-card__path--unassigned'
              : 'staffing-card__path'
          }
          data-testid="staffing-card-path"
        >
          {processPathName}
        </Text>
        {station && (
          <Text
            type="b100"
            truncate
            color="secondary"
            className="staffing-card__station"
            data-testid="staffing-card-station"
          >
            {station}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default AssociateRow;
